import { Button, Input, Tab, Tabs } from '@heroui/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import PhoneInput from 'components/Hero/PhoneInput'
import { TopBar } from 'components/Layout/TopBar'
import { TopBarContent } from 'components/Layout/TopBarProvider'
import GradientLoadingAnimation from 'components/Reusables/GradientLoadingAnimation'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import { queryClient } from 'utils/queryClient'
import { cn } from '@/lib/utils'
import { InboundTab } from './InboundTab'
import { OutboundTab } from './OutboundTab'

export default function SipConfiguration() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const phoneNumber = searchParams.get('phone_number') ? decodeURIComponent(searchParams.get('phone_number').trim()) : ''
  const [activeTab, setActiveTab] = useState('inbound')

  const methods = useForm({
    defaultValues: {
      phone_number: phoneNumber || '',
      inbound: {
        enabled: false,
        headers: [],
      },
      outbound: {
        enabled: false,
        sip_endpoint: '',
        options: {},
        headers: [],
      },
    },
  })

  const { control, handleSubmit, reset, setValue } = methods

  const inboundHeaders = useFieldArray({
    control,
    name: 'inbound.headers',
  })

  const outboundHeaders = useFieldArray({
    control,
    name: 'outbound.headers',
  })

  const { data: sipConfig, isLoading } = useQuery({
    queryKey: ['sipConfig', phoneNumber],
    queryFn: async () => {
      if (!phoneNumber)
        return null
      const response = await $fetch(`/v1/sip?phone_number=${phoneNumber}`)
      return response?.data
    },
    enabled: !!phoneNumber,
  })

  useEffect(() => {
    if (sipConfig) {
      const inboundConfig = sipConfig.inbound
      const outboundConfig = sipConfig.outbound

      const config = {
        phone_number: phoneNumber || '',
        inbound: {
          enabled: !!inboundConfig,
          headers: inboundConfig?.headers?.headers || [],
        },
        outbound: {
          enabled: !!outboundConfig,
          sip_endpoint: outboundConfig?.sip_endpoint || '',
          options: {
            sip_username: outboundConfig?.options?.sip_username || '',
            sip_password: outboundConfig?.options?.sip_password || '',
            transport: outboundConfig?.options?.transport || 'udp',
            secure_media: outboundConfig?.options?.secure_media || false,
            port: outboundConfig?.options?.port || 5060,
          },
          headers: outboundConfig?.headers?.headers || [],
        },
      }

      reset(config)
    }
  }, [sipConfig, phoneNumber, reset])

  const hasInboundEnabled = methods.watch('inbound.enabled')
  const hasOutboundEnabled = methods.watch('outbound.enabled')
  const toDelete = !!phoneNumber && !hasInboundEnabled && !hasOutboundEnabled

  const { mutate: saveConfig, isPending: isSaving } = useMutation({
    mutationFn: async (data) => {
      const phoneNumberToUse = data.phone_number ? `+${data.phone_number.replace(/\+/g, '')}` : (phoneNumber ? `+${phoneNumber.replace(/\+/g, '')}` : undefined)
      if (!phoneNumberToUse) {
        throw new Error('Phone number is required')
      }

      const cleanHeaders = (headers) => {
        if (!Array.isArray(headers)) {
          return []
        }
        return headers.map((h) => {
          const { id, ...rest } = h
          // Remove undefined properties from rest
          Object.keys(rest).forEach(key => rest[key] === undefined && delete rest[key])
          return rest
        })
      }

      const inboundHeadersPayload = cleanHeaders(data.inbound.headers)
      const outboundHeadersPayload = cleanHeaders(data.outbound.headers)

      let hasError = false

      const originalInboundEnabled = !!sipConfig?.inbound
      const originalOutboundEnabled = !!sipConfig?.outbound

      const formInboundEnabled = data.inbound.enabled
      const formOutboundEnabled = data.outbound.enabled

      // Detaching
      const directionsToDetach = []
      if (originalInboundEnabled && !formInboundEnabled) {
        directionsToDetach.push({ type: 'inbound' })
      }
      if (originalOutboundEnabled && !formOutboundEnabled) {
        directionsToDetach.push({ type: 'outbound' })
      }
      if (directionsToDetach.length > 0) {
        const res = await $fetch('/v1/sip/detach', {
          method: 'POST',
          body: { service: 'sip', phone_number: phoneNumberToUse, directions: directionsToDetach },
          ignoreResponseError: true,
        })
        if (res.errors) {
          const error = res.errors[0]?.message || 'Failed to detach SIP configuration'
          toast.error(error)
          hasError = true
        }
      }

      // Updating
      const updatesToApply = []
      if (originalInboundEnabled && formInboundEnabled) {
        const update = $fetch('/v1/sip/update', {
          method: 'POST',
          body: {
            phone_number: phoneNumberToUse,
            updates: {
              type: 'inbound',
              headers: { direction: 'inbound', headers: inboundHeadersPayload, passthrough: true },
            },
          },
          ignoreResponseError: true,
        })
        updatesToApply.push(update)
      }
      if (originalOutboundEnabled && formOutboundEnabled) {
        const update = $fetch('/v1/sip/update', {
          method: 'POST',
          body: {
            phone_number: phoneNumberToUse,
            updates: {
              type: 'outbound',
              sip_endpoint: data.outbound.sip_endpoint,
              options: data.outbound.options,
              headers: { direction: 'outbound', headers: outboundHeadersPayload, passthrough: true },
            },
          },
          ignoreResponseError: true,
        })
        updatesToApply.push(update)
      }

      try {
        const updatesResult = await Promise.all(updatesToApply)
        for (const update of updatesResult) {
          if (update.errors) {
            const error = update.errors[0]?.message || 'Failed to update SIP configuration'
            hasError = true
            toast.error(error)
          }
        }
      }
      catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to update SIP configuration')
        hasError = true
      }

      // Attaching
      const directionsToAttach = []
      if (!originalInboundEnabled && formInboundEnabled) {
        directionsToAttach.push({
          type: 'inbound',
          headers: {
            direction: 'inbound',
            headers: inboundHeadersPayload,
          },
        })
      }
      if (!originalOutboundEnabled && formOutboundEnabled) {
        directionsToAttach.push({
          type: 'outbound',
          sip_endpoint: data.outbound.sip_endpoint,
          options: data.outbound.options,
          headers: {
            direction: 'outbound',
            headers: outboundHeadersPayload,
          },
        })
      }
      if (directionsToAttach.length > 0) {
        const res = await $fetch('/v1/sip/attach', {
          method: 'POST',
          body: { phone_number: phoneNumberToUse, directions: directionsToAttach, service: 'sip' },
          ignoreResponseError: true,
        })
        if (res.errors) {
          const error = res.errors[0]?.message || 'Failed to attach SIP configuration'
          hasError = true
          toast.error(error)
        }
      }

      // If all is well, we can invalidate the query for the current phone number and throw a success toast
      if (!hasError) {
        toast.success('SIP configuration saved successfully')
        setSearchParams({ phone_number: phoneNumberToUse.startsWith('+') ? phoneNumberToUse.substring(1) : phoneNumberToUse })
        queryClient.invalidateQueries({ queryKey: ['sipConfig', phoneNumberToUse] })
        queryClient.invalidateQueries({ queryKey: ['inboundNumbers'] })
        if (toDelete) {
          navigate(`/dashboard/phone-numbers`)
        }
      }
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to edit SIP configuration')
    },
  })

  const handleSave = (data) => {
    saveConfig(data)
  }

  const addHeader = (direction) => {
    const newHeader = {
      type: 'custom',
      key: '',
      value: '',
    }

    if (direction === 'inbound') {
      inboundHeaders.append(newHeader)
    }
    else {
      outboundHeaders.append(newHeader)
    }
  }

  const removeHeader = (direction, index) => {
    if (direction === 'inbound') {
      inboundHeaders.remove(index)
    }
    else {
      outboundHeaders.remove(index)
    }
  }

  return (
    <div className="flex w-full flex-1">
      <TopBarContent>
        <TopBar.Title>
          SIP Configuration
          {phoneNumber && ` - +${phoneNumber}`}
        </TopBar.Title>
        <TopBar.Actions>
          <Button
            size="sm"
            variant="secondary"
            onPress={() => navigate(`/dashboard/phone-numbers`)}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Phone Numbers
          </Button>
        </TopBar.Actions>
      </TopBarContent>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleSave)} className="w-full px-5">
          <div className="mt-5 flex h-full flex-col">
            {
              isLoading
                ? (
                    <div className="flex w-full flex-1">
                      <div className="flex w-full items-center justify-center">
                        <GradientLoadingAnimation message="Loading SIP Configuration" variant="roseGarden" rounded="full" />
                      </div>
                    </div>
                  )
                : (
                    <>
                      {phoneNumber
                        ? (
                            <Input
                              label="Phone number"
                              isDisabled
                              name="phone_number"
                              variant="bordered"
                              radius="sm"
                              labelPlacement="outside"
                              value={`+${phoneNumber}`}
                            />
                          )
                        : (
                            <PhoneInput
                              isRequired
                              label="Phone Number"
                              value={methods.watch('phone_number')}
                              onChange={e => setValue('phone_number', e)}
                              key="phone_number"
                              name="phone_number"
                              control={methods.control}
                            />
                          )}

                      <div className="mt-6 flex h-full w-full flex-col gap-4">
                        <Tabs
                          aria-label="SIP Configuration"
                          className="w-full"
                          radius="none"
                          selectedKey={activeTab}
                          onSelectionChange={key => setActiveTab(key)}
                        >
                          <Tab key="inbound" title="Inbound">
                            <InboundTab
                              headers={inboundHeaders}
                              addHeader={addHeader}
                              removeHeader={removeHeader}
                            />
                          </Tab>

                          <Tab key="outbound" title="Outbound">
                            <OutboundTab
                              headers={outboundHeaders}
                              addHeader={addHeader}
                              removeHeader={removeHeader}
                            />
                          </Tab>
                        </Tabs>

                        <div className="flex justify-end gap-2 pb-10">
                          <Button
                            type="submit"
                            isDisabled={!phoneNumber && !hasInboundEnabled && !hasOutboundEnabled}
                            className={cn(['w-fit shrink-0', !toDelete && `
                              bg-blue-500 text-white
                            `])}
                            variant={toDelete ? 'bordered' : undefined}
                            radius="none"
                            color={toDelete ? 'danger' : undefined}
                            isLoading={isSaving}
                          >
                            {toDelete ? 'Delete SIP Number' : 'Save Configuration'}
                          </Button>
                        </div>
                      </div>
                    </>
                  )
            }
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
